
import * as React from "react";
import {
  Box,
  Chip,
  Tooltip,
  Typography,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Card,
  CardContent,
  Grid,
  Button,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useContext, useEffect, useState, useMemo } from "react";
import VisibilityIcon from "@mui/icons-material/Visibility";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import PeopleIcon from "@mui/icons-material/People";
import CategoryIcon from "@mui/icons-material/Category";
import DownloadIcon from "@mui/icons-material/Download";
import * as XLSX from "xlsx";
import {
  getRequestWithToken,
} from "../../utils/ApiRequests";
import { GlobalContext } from "../../components/GlobalStateProvide/GlobalStateProvider";
import ReusableLoader from "../../components/ReusableLoader/ReusableLoader";
import { checkPermission } from "../../utils/userPermissions";
import Unauthorized from "../../components/unauthorized/Unauthorized";
import PropTypes from "prop-types";
import {
  MaterialReactTable,
  useMaterialReactTable,
} from "material-react-table";

const PlusCandidates = () => {
  const [employerData, setEmployerData] = useState([]);
  const [employerDeleted, setEmployerDeleted] = useState(false);
  const navigate = useNavigate();
  const { handleSnackBar } = useContext(GlobalContext);
  const [showLoader, setShowLoader] = useState(true);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [openStateChangeModal, setOpenStateChangeModal] = useState(false);
  const [candidateId, setCandidateId] = useState("");
  const [pageNumber, setPageNumber] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [rowCount, setRowCount] = useState(0);
  const [loadingDataInGrid, setLoadingDataInGrid] = useState(null);
  const [canCreate, setCanCreate] = useState(false);
  const [canDelete, setCanDelete] = useState(false);
  const [canUpdate, setCanUpdate] = useState(false);
  const [canRead, setCanRead] = useState(false);

  const [isAccess, setisAccess] = useState([]);
  const [isIncludesValue, setisIncludesValue] = useState(null);

  const [tabvalue, setTabValue] = React.useState(0);

  const [profileStatus, setProfileStatus] = useState(null);
  const [employerID, setEmployerID] = useState(null);
  const [searchBarValue, setSearchBarValue] = useState("");

  const [columnFilters, setColumnFilters] = useState([]);
  const [globalFilter, setGlobalFilter] = useState("");
  const [sorting, setSorting] = useState([]);
  const [isRefetching, setIsRefetching] = useState(false);
  const [isError, setIsError] = useState(false);

  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10,
  });

  // New states for role filter and roles list
  const [selectedRole, setSelectedRole] = useState("");
  const [rolesList, setRolesList] = useState([]);
  const [totalCandidates, setTotalCandidates] = useState(0);
  const [totalRoles, setTotalRoles] = useState(0);

  const [showDeletedRows, setShowDeletedRows] = useState(true);


  const filteredRows = showDeletedRows
    ? employerData
    : employerData.filter(
      (employer) =>
        employer.hasOwnProperty("deleted") &&
        employer.deleted.is_deleted === false
    );

  // Fetch roles list
  const fetchRoles = async () => {
    try {
      const response = await getRequestWithToken("admin/plus-roles?page=1&limit=100");
      if (response?.data?.data?.plusRoles) {
        setRolesList(response.data.data.plusRoles);
        setTotalRoles(response.data.data.pagination?.totalItems || response.data.data.plusRoles.length);
      }
    } catch (error) {
      handleSnackBar(
        true,
        error?.response?.data?.message || "Failed to fetch roles",
        "error"
      );
    }
  };

  // Generate dynamic columns based on roleAttributeValue fields
  const dynamicColumns = useMemo(() => {
    if (!employerData || employerData.length === 0) return [];

    const fieldKeysSet = new Set();
    const locationFields = new Set();

    // Collect all unique field keys and identify location fields
    employerData.forEach((record) => {
      const fields = record?.roleAttributeValue?.fields;
      if (Array.isArray(fields)) {
        fields.forEach((field) => {
          if (field.key) {
            fieldKeysSet.add(field.key);
            if (field.type === "location") {
              locationFields.add(field.key);
            }
          }
        });
      }
    });

    // Create columns for each unique field key
    const allColumns = [];

    Array.from(fieldKeysSet).forEach((fieldKey) => {
      if (locationFields.has(fieldKey)) {
        // For location fields, create separate columns for each sub-property
        const locationSubFields = ["address", "city", "state", "district", "pincode"];

        locationSubFields.forEach((subField) => {
          allColumns.push({
            accessorFn: (row) => {
              const fields = row?.roleAttributeValue?.fields;
              if (!Array.isArray(fields)) return "---";

              const field = fields.find((f) => f.key === fieldKey);
              if (!field || field.type !== "location") return "---";

              const locationValue = field.value;
              if (typeof locationValue !== "object" || locationValue === null) {
                return "---";
              }

              const subValue = locationValue[subField];
              return subValue ? String(subValue) : "---";
            },
            header: `${fieldKey.charAt(0).toUpperCase() + fieldKey.slice(1).replace(/_/g, " ")} - ${subField.charAt(0).toUpperCase() + subField.slice(1)}`,
            id: `field_${fieldKey}_${subField}`,
            size: 180,
            enableColumnFilter: false,
            Cell: ({ cell }) => {
              const value = cell.getValue();
              return (
                <Tooltip title={value}>
                  <div style={{
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    maxWidth: "180px"
                  }}>
                    {value}
                  </div>
                </Tooltip>
              );
            },
          });
        });
      } else {
        // For non-location fields, use existing logic
        allColumns.push({
          accessorFn: (row) => {
            const fields = row?.roleAttributeValue?.fields;
            if (!Array.isArray(fields)) return "---";

            const field = fields.find((f) => f.key === fieldKey);
            if (!field) return "---";

            // Handle different field types
            const value = field.value;

            if (value === null || value === undefined || value === "") {
              return "---";
            }

            // Handle arrays (multiselect, multiSelectCheckbox)
            if (Array.isArray(value)) {
              return value.length > 0 ? value.join(", ") : "---";
            }

            // Handle objects
            if (typeof value === "object" && value !== null) {
              return JSON.stringify(value);
            }

            // Handle boolean
            if (typeof value === "boolean") {
              return value ? "Yes" : "No";
            }

            // Handle date
            if (field.type === "date" && value) {
              return new Date(value).toLocaleDateString("en-US");
            }

            return String(value);
          },
          header: fieldKey.charAt(0).toUpperCase() + fieldKey.slice(1).replace(/_/g, " "),
          id: `field_${fieldKey}`,
          size: 200,
          enableColumnFilter: false,
          Cell: ({ cell }) => {
            const value = cell.getValue();
            return (
              <Tooltip title={value}>
                <div style={{
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  maxWidth: "200px"
                }}>
                  {value}
                </div>
              </Tooltip>
            );
          },
        });
      }
    });

    return allColumns;
  }, [employerData]);

  const columns = useMemo(() => {
    const staticColumns = [
      {
        accessorKey: "id",
        header: "S.No.",
        size: 80,
        enableColumnFilter: false,
        Cell: ({ row, table }) => {
          const pageIndex = table.getState().pagination.pageIndex;
          const pageSize = table.getState().pagination.pageSize;
          const rowIndex = row.index;
          return <span>{rowIndex + 1 + pageIndex * pageSize}</span>;
        },
      },
      {
        accessorKey: "roleAttributeName",
        header: "Role",
        size: 150,
        enableColumnFilter: false,
        Cell: ({ cell }) => (
          <Chip
            label={cell.getValue() || "---"}
            color="primary"
            variant="outlined"
            size="small"
          />
        ),
      },
      {
        accessorKey: "mobile",
        header: "Mobile",
        size: 150,
        enableColumnFilter: false,
      },
      {
        accessorKey: "candidateId",
        header: "Candidate ID",
        size: 200,
        enableColumnFilter: false,
      },
      {
        accessorKey: "createdAt",
        header: "Created At",
        size: 200,
        enableColumnFilter: false,
        Cell: ({ cell }) => (
          <span>
            {new Date(cell.getValue()).toLocaleString("en-US", {
              timeZone: "Asia/Kolkata",
            })}
          </span>
        ),
      },
      {
        accessorKey: "updatedAt",
        header: "Updated At",
        size: 200,
        enableColumnFilter: false,
        Cell: ({ cell }) => (
          <span>
            {new Date(cell.getValue()).toLocaleString("en-US", {
              timeZone: "Asia/Kolkata",
            })}
          </span>
        ),
      },
    ];

    const actionColumn = {
      accessorKey: "z_action",
      header: "Action",
      size: 150,
      enableColumnFilter: false,
      Cell: ({ row }) =>
        row.original.deleted?.isDeleted ? (
          <Box style={{ display: "flex", gap: "1rem" }}>
            {canUpdate && <EditIcon color="disabled" />}
            {canDelete && <DeleteIcon color="disabled" />}
          </Box>
        ) : (
          <Box style={{ display: "flex", gap: "1rem" }}>
            {canRead && (
              <Tooltip title="View">
                <VisibilityIcon
                  sx={{ cursor: "pointer" }}
                  color="primary"
                  onClick={() =>
                    window.open(`/plus-candidate/detail/${row.original._id}`, "_blank")
                  }
                />
              </Tooltip>
            )}
            {canUpdate && (
              <EditIcon
                sx={{ cursor: "pointer" }}
                color="primary"
                onClick={() =>
                  navigate("/plus-candidate/edit", { state: row.original })
                }
              />
            )}
            {canDelete && (
              <DeleteIcon
                sx={{ cursor: "pointer" }}
                color="error"
                onClick={() => {
                  setOpenDeleteModal(true);
                  setCandidateId(row.original._id);
                }}
              />
            )}
          </Box>
        ),
    };

    const deletedColumn = {
      accessorKey: "is_deleted",
      header: "Status",
      enableColumnFilter: false,
      size: 120,
      Cell: ({ row }) => {
        if (row.original.deleted?.isDeleted) {
          return (
            <span>
              <Chip variant="outlined" label="Deleted" color="error" size="small" />
            </span>
          );
        }
        return (
          <Chip variant="outlined" label="Active" color="success" size="small" />
        );
      },
    };

    return [...staticColumns, ...dynamicColumns, deletedColumn];
  }, [dynamicColumns, canRead, canUpdate, canDelete, navigate]);


  const getAllEmployers = async () => {
    setLoadingDataInGrid(true);
    try {
      const roleParam = selectedRole ? `&role=${selectedRole}` : "";
      let response = await getRequestWithToken(
        `admin/plus-attribute-candidate?page=${pagination.pageIndex + 1}&limit=${pagination.pageSize}${roleParam}`
      );
      setRowCount(response?.data?.pagination?.totalCount || 0);
      setTotalCandidates(response?.data?.pagination?.totalCount || 0);
      setEmployerData(response.data.data || []);
      setLoadingDataInGrid(false);
      setShowLoader(false);
    } catch (error) {
      handleSnackBar(
        true,
        error?.response?.data?.msg || error?.message,
        "error"
      );
      setLoadingDataInGrid(false);
      setShowLoader(false);
      setEmployerData([]);
    }
  };

  useEffect(() => {
    fetchRoles();
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      if (!employerData.length) {
        setLoadingDataInGrid(true);
      } else {
        setIsRefetching(true);
      }

      try {
        const roleParam = selectedRole ? `&role=${selectedRole}` : "";
        const response = await getRequestWithToken(
          `admin/plus-attribute-candidate?page=${pagination.pageIndex + 1}&limit=${pagination.pageSize}${roleParam}`
        );

        setEmployerData(response?.data?.data || []);
        setRowCount(response?.data?.pagination?.totalCount || 0);
        setTotalCandidates(response?.data?.pagination?.totalCount || 0);
        setIsError(false);
        setLoadingDataInGrid(false);
        setShowLoader(false);
      } catch (error) {
        setIsError(true);
        handleSnackBar(
          true,
          error?.response?.data?.message || error?.message,
          "error"
        );
        setLoadingDataInGrid(false);
        setEmployerData([]);
      }
      setLoadingDataInGrid(false);
      setIsRefetching(false);
      setShowLoader(false);
    };
    fetchData();
  }, [
    pagination.pageIndex,
    pagination.pageSize,
    selectedRole,
  ]);


  const checkPermissionFunc = () => {
    const resourceToUpdate = "Plus";
    const permissions = checkPermission(resourceToUpdate);
    setCanCreate(permissions.canCreate);
    setCanDelete(permissions.canDelete);
    setCanUpdate(permissions.canUpdate);
    setCanRead(permissions.canRead);
  };

  const againCheckPermissions = () => {
    const checkAccesInArray = localStorage.getItem("resourceNames");
    setisAccess(checkAccesInArray);
    isIncludes();
  };

  const isIncludes = () => {
    setisIncludesValue(isAccess.includes("Plus"));
  };

  useEffect(() => {
    checkPermissionFunc();
    againCheckPermissions();
  }, [
    pageSize,
    pageNumber,
    isIncludesValue,
    profileStatus,
    tabvalue,
    searchBarValue,
  ]);

  const handleRoleChange = (event) => {
    setSelectedRole(event.target.value);
    setPagination({ pageIndex: 0, pageSize: pagination.pageSize });
  };

  const handleExportToExcel = () => {
    try {
      // Prepare data for export
      const exportData = filteredRows.map((row, index) => {
        const baseData = {
          "S.No.": index + 1,
          "Role": row.roleAttributeName || "---",
          "Mobile": row.mobile || "---",
          "Candidate ID": row.candidateId || "---",
          "Created At": new Date(row.createdAt).toLocaleString("en-US", {
            timeZone: "Asia/Kolkata",
          }),
          "Updated At": new Date(row.updatedAt).toLocaleString("en-US", {
            timeZone: "Asia/Kolkata",
          }),
        };

        // Add dynamic fields
        const fields = row?.roleAttributeValue?.fields;
        if (Array.isArray(fields)) {
          fields.forEach((field) => {
            const fieldName = field.key.charAt(0).toUpperCase() + field.key.slice(1).replace(/_/g, " ");
            let value = field.value;

            // Handle location fields separately
            if (field.type === "location" && typeof value === "object" && value !== null) {
              const locationSubFields = ["address", "city", "state", "district", "pincode"];
              locationSubFields.forEach((subField) => {
                const subFieldName = `${fieldName} - ${subField.charAt(0).toUpperCase() + subField.slice(1)}`;
                const subValue = value[subField];
                baseData[subFieldName] = subValue ? String(subValue) : "---";
              });
            } else if (value === null || value === undefined || value === "") {
              baseData[fieldName] = "---";
            } else if (Array.isArray(value)) {
              baseData[fieldName] = value.length > 0 ? value.join(", ") : "---";
            } else if (typeof value === "object" && value !== null) {
              baseData[fieldName] = JSON.stringify(value);
            } else if (typeof value === "boolean") {
              baseData[fieldName] = value ? "Yes" : "No";
            } else if (field.type === "date" && value) {
              baseData[fieldName] = new Date(value).toLocaleDateString("en-US");
            } else {
              baseData[fieldName] = String(value);
            }
          });
        }

        // Add status
        baseData["Status"] = row.deleted?.isDeleted ? "Deleted" : "Active";

        return baseData;
      });

      // Create worksheet
      const ws = XLSX.utils.json_to_sheet(exportData);

      // Set column widths
      const columnWidths = Object.keys(exportData[0] || {}).map(() => ({ wch: 20 }));
      ws['!cols'] = columnWidths;

      // Create workbook
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Plus Candidates");

      // Generate filename with timestamp
      const timestamp = new Date().toISOString().split('T')[0];
      const roleFilter = selectedRole ? `_${selectedRole}` : "";
      const filename = `Plus_Candidates${roleFilter}_${timestamp}.xlsx`;

      // Save file
      XLSX.writeFile(wb, filename);

      handleSnackBar(true, "Excel file downloaded successfully!", "success");
    } catch (error) {
      handleSnackBar(true, "Failed to export Excel file", "error");
      console.error("Export error:", error);
    }
  };

  function CustomTabPanel(props) {
    const { children, value, index, ...other } = props;

    return (
      <div
        role="tabpanel"
        hidden={value !== index}
        id={`simple-tabpanel-${index}`}
        aria-labelledby={`simple-tab-${index}`}
        {...other}
      >
        {value === index && (
          <Box sx={{ p: 3 }}>
            <Box>{children}</Box>
          </Box>
        )}
      </div>
    );
  }

  CustomTabPanel.propTypes = {
    children: PropTypes.node,
    index: PropTypes.number.isRequired,
    value: PropTypes.number.isRequired,
  };


  const [openTask, setOpenTask] = useState(false);

  const handleCloseTask = () => setOpenTask(false);

  const table = useMaterialReactTable({
    columns,
    data: filteredRows,
    initialState: { showColumnFilters: false },
    manualFiltering: true,
    manualPagination: true,
    manualSorting: true,
    enableStickyHeader: true,
    enableStickyFooter: true,
    enableColumnPinning: true,
    enableColumnResizing: true,
    enableGlobalFilter: false,
    enableSorting: false,
    muiSkeletonProps: {
      animation: "wave",
    },
    muiLinearProgressProps: {
      style: {
        backgroundColor: "#ff671f", // Custom orange color
      },
    },
    muiCircularProgressProps: {
      color: "secondary",
    },
    muiToolbarAlertBannerProps: isError
      ? {
        color: "error",
        children: "No Data",
      }
      : undefined,
    onColumnFiltersChange: setColumnFilters,
    onGlobalFilterChange: setGlobalFilter,
    onPaginationChange: setPagination,
    onSortingChange: setSorting,
    rowCount,
    state: {
      columnFilters,
      globalFilter,
      isLoading: loadingDataInGrid,
      pagination,
      showAlertBanner: isError,
      showProgressBars: isRefetching,
      sorting,
    },
    renderBottomToolbarCustomActions: ({ table }) => {
      const { pageIndex, pageSize } = table.getState().pagination;
      console.log("pageIndex:", pageIndex, "pageSize:", pageSize, "rowCount:", rowCount);
      const totalPages = Math.ceil(rowCount / pageSize);
      return (
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Typography variant="body2" style={{ marginLeft: "10px" }}>
            Page {pageIndex + 1} of {totalPages}
          </Typography>
        </Box>
      );
    },
    renderTopToolbarCustomActions: ({ table }) => {
      const pinnedColumns = table.getState().columnPinning;
      return (
        <Box>
          <Chip
            label={`Left Pinned: ${pinnedColumns.left.length}`}
            color="primary"
            sx={{ mr: 1 }}
          />
          <Chip
            label={`Right Pinned: ${pinnedColumns.right.length}`}
            color="secondary"
          />
        </Box>
      );
    },
  });

  return isIncludesValue ? (
    <Box className="employers-section">




      {/* Statistics Cards */}
      <Grid container spacing={3} >
        <Grid item xs={12} sm={4} md={4}>
          {/* Role Filter */}
          <Box >
            <FormControl sx={{ minWidth: 300, width: "100%" }}>
              <InputLabel id="role-filter-label">Filter by Role</InputLabel>
              <Select
                labelId="role-filter-label"
                id="role-filter"
                value={selectedRole}
                label="Filter by Role"
                onChange={handleRoleChange}
              >
                <MenuItem value="">
                  <em>All Roles</em>
                </MenuItem>
                {rolesList.map((role) => (
                  <MenuItem key={role?._id} value={role?.name.toLowerCase()}>
                    {role?.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </Grid>
        <Grid item xs={12} sm={4} md={4}>
          <Card elevation={3}>
            <CardContent style={{ padding: '16px 16px 8px 16px' }}>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography variant="h6" color="text.secondary" gutterBottom>
                    Total Candidates
                  </Typography>
                  <Typography variant="h4" component="div" fontWeight="bold" color="primary">
                    {totalCandidates}
                  </Typography>
                </Box>
                <PeopleIcon sx={{ fontSize: 60, color: "primary.main", opacity: 0.3 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4} md={4}>
          <Card elevation={3}>
            <CardContent style={{ padding: '16px 16px 8px 16px' }}>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography variant="h6" color="text.secondary" gutterBottom>
                    Total Roles
                  </Typography>
                  <Typography variant="h4" component="div" fontWeight="bold" color="secondary">
                    {totalRoles}
                  </Typography>
                </Box>
                <CategoryIcon sx={{ fontSize: 60, color: "secondary.main", opacity: 0.3 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Export Button */}
      <Box sx={{ mt: 2, mb: 2, display: 'flex', justifyContent: 'flex-end' }}>
        <Button
          variant="contained"
          color="success"
          startIcon={<DownloadIcon />}
          onClick={handleExportToExcel}
          disabled={!filteredRows || filteredRows.length === 0}
        >
          Export to Excel
        </Button>
      </Box>

      <Box sx={{ width: "100%" }}>
        <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
        </Box>

        {showLoader ? <ReusableLoader /> : <MaterialReactTable table={table} />}
      </Box>
    </Box>
  ) : (
    <Unauthorized />
  );
};
export default PlusCandidates;
